import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/company-questions.js
import { trackUserChange } from '@/utils/changeDetection';

/**
 * CompanyQuestionsHandler
 * 
 * Manages company questions data with sophisticated features:
 * - Loading and saving data with proper caching and structure preservation
 * - Real-time updates via WebSocket integration with standardized event handling
 * - Debounced saving with field-level locking for concurrent edits
 * - Optimistic UI updates with automatic conflict resolution
 * - Change detection for unsaved modifications
 * - Handles transitions between draft and submitted states
 */
export class CompanyQuestionsHandler {
  /**
   * Initialize the company questions handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;
    this.currentVersion = Date.now();
    this.saveQueue = null;
    this.saveTimer = null;
    this.saveDelay = 800; // ms - debounce delay for text inputs
    this.isSaving = false;
    this.fieldLocks = new Set(); // Track fields currently being saved
    this.lastSavedData = null; // Track last successfully saved data

    // Initialize event handlers map for WebSocket integration
    this._initEventHandlers();

    // Register with WebSocketHandler when available
    this._registerWithWebSocketHandler();
  }

  /**
   * Initialize event handlers map for standardized WebSocket events
   * @private
   */
  _initEventHandlers() {
    this._eventHandlers = {
      // Type+action handlers
      'company-questions:update': this._handleQuestionsUpdate.bind(this),
      'company-questions:populate': this._handlePopulateEvent.bind(this),
      'submission:complete': this._handleSubmissionEvent.bind(this),

      // Type-level handlers
      'company-questions': this._handleGenericQuestionsEvent.bind(this)
    };
  }

  /**
   * Register with the centralized WebSocketHandler
   * @private
   */
  _registerWithWebSocketHandler() {
    if (this.store.webSocketHandler) {
      this.store.webSocketHandler.registerHandler(
        'CompanyQuestionsHandler',
        this.handleWebSocketEvent.bind(this),
        [
          'company-questions',
          'company-questions:update',
          'company-questions:populate',
          'submission:complete'
        ],
        this
      );
    }
  }

  /**
   * Primary WebSocket event handler implementing the standardized interface
   * @param {Object} event - Normalized event object
   * @param {Object} options - Additional options
   * @returns {Promise<boolean>} - Success status
   */
  async handleWebSocketEvent(event, options = {}) {
    try {
      // Validate event structure
      if (!this._validateEvent(event)) {
        return false;
      }

      // Process event based on type and action
      return await this._processEvent(event, options);
    } catch (error) {
      this._handleEventError(event, error);
      return false;
    }
  }

  /**
   * Validate that event has required structure
   * @param {Object} event - The event to validate
   * @returns {boolean} - Whether the event is valid
   * @private
   */
  _validateEvent(event) {
    if (!event || !event.type) {
      return false;
    }
    return true;
  }

  /**
   * Process event based on type and action
   * @param {Object} event - The event to process
   * @param {Object} options - Processing options
   * @returns {Promise<boolean>} - Whether event was handled
   * @private
   */
  async _processEvent(event, options) {
    const { type, action } = event;

    // Try the most specific handler first (type:action)
    const specificKey = action ? `${type}:${action}` : type;
    if (this._eventHandlers[specificKey]) {
      return await this._eventHandlers[specificKey](event, options);
    }

    // Fall back to type-level handler
    if (this._eventHandlers[type]) {
      return await this._eventHandlers[type](event, options);
    }

    // Default behavior for unhandled events
    return this._handleDefaultRefresh(event, options);
  }

  /**
   * Handle errors during event processing
   * @param {Object} event - The event being processed
   * @param {Error} error - The error that occurred
   * @private
   */
  _handleEventError(event, error) {
    console.error(`Error processing ${event.type}:${event.action || ''} event:`, error);
    this.store.handleError('companyQuestions', error);
  }

  /**
   * Handle questions update events
   * @param {Object} event - The event to handle
   * @returns {Promise<boolean>} - Success status
   * @private
   */
  async _handleQuestionsUpdate(event) {
    // Skip handling specific updates if we have a pending save
    if (this.saveQueue || this.saveTimer || this.isSaving) {
      return true;
    }

    // Check for version conflicts
    const currentData = this.store.state.data.companyQuestions?.data;

    // If we have a version field, check for conflicts
    if (currentData && currentData._version && event._version) {
      // Skip if our version is newer
      if (currentData._version > event._version) {
        return true;
      }
    }

    // Check if assessment status has changed from draft to submitted
    if (currentData &&
      currentData.assessmentStatus === 'draft' &&
      event.assessmentStatus === 'submitted') {

      // Update status in local state with a new object
      this.store.state.data.companyQuestions = {
        ...this.store.state.data.companyQuestions,
        data: {
          ...currentData,
          assessmentStatus: 'submitted'
        }
      };

      this.currentVersion = event._version || Date.now();

      // Update last saved data for change detection
      this.lastSavedData = JSON.stringify(this.store.state.data.companyQuestions.data);

      // Notify subscribers
      this.store.notifySubscribers('companyQuestions', this.store.state.data.companyQuestions.data);

      return true;
    }

    // For other updates, reload the data
    await this.loadCompanyQuestions(true);
    return true;
  }

  /**
   * Handle populate events (data refreshed on server)
   * @param {Object} event - The event to handle
   * @returns {Promise<boolean>} - Success status
   * @private
   */
  async _handlePopulateEvent(_event) {
    await this.loadCompanyQuestions(true);
    return true;
  }

  /**
   * Handle submission events (when survey is submitted)
   * Updates assessment status without manipulating timestamps
   * @param {Object} event - The event to handle
   * @returns {Promise<boolean>} - Success status
   * @private
   */
  async _handleSubmissionEvent(_event) {
    const currentData = this.store.state.data.companyQuestions?.data;

    if (currentData) {
      if (currentData.assessmentStatus !== 'submitted') {
        // Create a new data object with updated status - preserving all other fields
        const updatedData = {
          ...currentData,
          assessmentStatus: 'submitted'
        };

        // Update store with completely new references
        this.store.state.data.companyQuestions = {
          ...this.store.state.data.companyQuestions,
          data: updatedData
        };

        // Notify subscribers of the update
        this.store.notifySubscribers('companyQuestions', this.store.state.data.companyQuestions);

        // Update current version for conflict resolution
        this.currentVersion = Date.now();

        // Update last saved data for change detection
        this.lastSavedData = JSON.stringify(updatedData);
      }
    }

    return true;
  }

  /**
   * Handle generic questions events without specific action
   * @param {Object} event - The event to handle
   * @returns {Promise<boolean>} - Success status
   * @private
   */
  async _handleGenericQuestionsEvent(event) {
    return this._handleDefaultRefresh(event);
  }

  /**
   * Default handler for unrecognized events
   * @param {Object} event - The event to handle
   * @param {Object} options - Processing options
   * @returns {Promise<boolean>} - Success status
   * @private
   */
  async _handleDefaultRefresh(event, options = {}) {
    // Skip silent refresh if option is set
    if (options.silent) return false;

    // Schedule refresh with debouncing to avoid multiple rapid refreshes
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(async () => {
      await this.loadCompanyQuestions(true);
      this.debounceTimeout = null;
    }, 300);

    return true;
  }

  /**
   * Load company questions from API with improved caching control
   * @param {boolean} forceRefresh - Whether to force a refresh bypassing cache
   * @returns {Promise<Object>} The company questions data
   * @throws {Error} If API request fails or no project ID is available
   */
  async loadCompanyQuestions(forceRefresh = false) {
    try {
      this.store.state.loading.companyQuestions = true;

      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error('No project ID available');
      }

      // Construct URL with cache-busting if forceRefresh is true
      let url = `${process.env.GATSBY_STRAPI_URL}/api/company-questions/project/${projectId}`;
      if (forceRefresh) {
        url += `?_t=${Date.now()}`; // Add timestamp to force fresh data
      }

      // Make API request with proper cache control
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getStrapiToken()}`,
          'Cache-Control': forceRefresh ? 'no-cache, no-store' : 'default'
        },
        cache: forceRefresh ? 'no-cache' : 'default'
      });

      // Create and return empty data for 404
      if (response.status === 404) {
        const emptyData = {
          questionnaire: [],
          footprints: [],
          reductions: [],
          assessmentStatus: 'draft',
          _version: Date.now()
        };

        this._updateStoreData(emptyData);
        return emptyData;
      }

      // Handle other errors
      if (!response.ok) {
        throw new Error(`Failed to fetch: HTTP ${response.status}`);
      }

      // Parse response
      const { data } = await response.json();

      // Store as last saved data for change detection
      this.lastSavedData = JSON.stringify(data);

      // Update current version
      this.currentVersion = data._version || Date.now();

      // Update store data
      this._updateStoreData(data);

      return data;

    } catch (error) {
      console.error('Company questions load failed:', error);
      this.store.handleError('companyQuestions', error);
      throw error;
    } finally {
      this.store.state.loading.companyQuestions = false;
    }
  }

  /**
   * Update store data and notify subscribers
   * @param {Object} data - Data to update in store
   * @private
   */
  _updateStoreData(data) {
    // Create completely new object references for the update
    this.store.state.data.companyQuestions = {
      data: data,
      meta: { lastSync: new Date().toISOString() }
    };

    // Notify subscribers
    this.store.notifySubscribers('companyQuestions', this.store.state.data.companyQuestions.data);
  }

  /**
   * Update company questions data with debounced or immediate saving
   * @param {Object} data - Updated data object to save
   * @param {boolean} immediate - Whether to save immediately (true) or use debounce (false)
   * @param {string} fieldId - Optional field ID being saved (for field-level locks)
   * @returns {Promise<Object>} API response data or updated local data
   * @throws {Error} If no data provided or API request fails
   */
  async updateData(data, immediate = false, fieldId = null) {
    if (!data) {
      throw new Error('No data provided for update');
    }

    try {
      // Track user change
      trackUserChange();

      // Create a deep copy to avoid reference issues
      const dataToUpdate = JSON.parse(JSON.stringify(data));

      // Ensure TimestampOfAnswers is present and valid
      if (!dataToUpdate.TimestampOfAnswers) {
        // Get from current store data or use today's date
        const currentData = this.store.state.data.companyQuestions?.data;
        dataToUpdate.TimestampOfAnswers = currentData?.TimestampOfAnswers || new Date().toISOString().split('T')[0];
      }

      // Save payload logging silenced

      // Get project ID
      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error('No project ID available');
      }

      // Update version
      dataToUpdate._version = Date.now();
      this.currentVersion = dataToUpdate._version;

      // Update local state immediately with a new object
      this._updateStoreData(dataToUpdate);

      // Always notify that we're saving - with field ID if provided
      this._notifySaveStatus('saving', null, fieldId);

      // If a specific field is being saved, lock it
      if (fieldId) {
        this.fieldLocks.add(fieldId);
      }

      // Clear existing timer to debounce rapid changes
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
        this.saveTimer = null;
      }

      // For immediate saves (checkboxes, etc.), skip the queue
      if (immediate) {
        return this._saveDataToAPI(dataToUpdate, fieldId);
      }

      // Otherwise, queue the data for debounced save
      this.saveQueue = { ...dataToUpdate }; // Create a new object for the queue

      // Return a promise that resolves when save is queued
      return new Promise((resolve) => {
        this.saveTimer = setTimeout(async () => {
          try {
            const result = await this._performSave(fieldId);
            resolve(result);
          } catch (error) {
            this._handleSaveError(error, fieldId);
            resolve(this.store.state.data.companyQuestions.data);
          }
        }, this.saveDelay);
      });
    } catch (error) {
      this._handleSaveError(error, fieldId);
      throw error;
    }
  }

  /**
   * Save data immediately - especially for checkbox/switch changes
   * @param {Object} data - Data to save immediately
   * @param {string} fieldId - Optional field ID being saved
   * @returns {Promise<Object>} The saved data
   */
  async saveImmediately(data, fieldId = null) {
    // Track user change
    trackUserChange();
    return this.updateData(data, true, fieldId);
  }

  /**
   * Check if there are unsaved changes by comparing with last saved state
   * @returns {boolean} True if there are unsaved changes
   */
  hasUnsavedChanges() {
    const currentData = this.store.state.data.companyQuestions?.data;
    if (!currentData || !this.lastSavedData) return false;

    // Compare current data with last saved data (ignoring version field)
    const currentForComparison = { ...currentData };
    delete currentForComparison._version;

    const lastSaved = JSON.parse(this.lastSavedData);
    delete lastSaved._version;

    return JSON.stringify(currentForComparison) !== JSON.stringify(lastSaved);
  }

  /**
   * Directly save data to API without debounce
   * @param {Object} data - Data to save
   * @param {string} fieldId - Optional field ID being saved
   * @returns {Promise<Object>} API response data
   * @private
   */
  async _saveDataToAPI(data, fieldId = null) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error('No project ID available');
      }

      // Set saving flag
      this.isSaving = true;

      // Cancel any pending saves
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
        this.saveTimer = null;
      }

      // Clear the save queue
      this.saveQueue = null;

      // Make API request and wait for completion
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/company-questions/project/${projectId}/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({ data: data })
        }
      );

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `HTTP error ${response.status}` };
        }
        // Extract a string message — errorData.error may be an object (Strapi error format)
        const errMsg = typeof errorData?.error === 'string'
          ? errorData.error
          : errorData?.error?.message || errorData?.message || `API error: ${response.status}`;
        throw new Error(errMsg);
      }

      // Process successful save
      const responseData = await response.json();

      // Update version if provided
      if (responseData.data._version) {
        this.currentVersion = Math.max(this.currentVersion, responseData.data._version);

        // Create a new data object with the updated version
        const updatedData = {
          ...this.store.state.data.companyQuestions.data,
          _version: this.currentVersion
        };

        // Update store with a new object
        this.store.state.data.companyQuestions = {
          ...this.store.state.data.companyQuestions,
          data: updatedData
        };
      }

      // Ensure TimestampOfAnswers is preserved
      if (responseData.data.TimestampOfAnswers) {
        // Create a new data object with the updated timestamp
        const updatedData = {
          ...this.store.state.data.companyQuestions.data,
          TimestampOfAnswers: responseData.data.TimestampOfAnswers
        };

        // Update store with a new object
        this.store.state.data.companyQuestions = {
          ...this.store.state.data.companyQuestions,
          data: updatedData
        };
      }

      // Update meta data with a new object
      this.store.state.data.companyQuestions = {
        ...this.store.state.data.companyQuestions,
        meta: {
          ...this.store.state.data.companyQuestions.meta,
          lastSync: new Date().toISOString()
        }
      };

      // Store as last saved data for change detection
      this.lastSavedData = JSON.stringify(this.store.state.data.companyQuestions.data);

      // Emit 'saved' status with field ID if applicable
      this._notifySaveStatus('saved', null, fieldId);

      // Clear field lock if it exists
      if (fieldId) {
        this.fieldLocks.delete(fieldId);
      }

      return responseData.data;
    } catch (error) {
      this._handleSaveError(error, fieldId);
      throw error;
    } finally {
      // Clear saving flag
      this.isSaving = false;
    }
  }

  /**
   * Perform the actual save to API for debounced operations
   * @param {string} fieldId - Optional field ID being saved
   * @returns {Promise<Object>} API response data
   * @private
   */
  async _performSave(fieldId = null) {
    if (!this.saveQueue || this.isSaving) {
      return this.store.state.data.companyQuestions.data;
    }

    const dataToSave = { ...this.saveQueue }; // Create a copy of the queue data
    this.saveQueue = null;
    this.isSaving = true;

    try {
      const result = await this._saveDataToAPI(dataToSave, fieldId);
      return result;
    } finally {
      this.isSaving = false;

      // Check if there's a new save queued up
      if (this.saveQueue) {
        // Small delay before processing next save
        setTimeout(() => this._performSave(), 100);
      }
    }
  }

  /**
   * Notify save status to subscribers
   * @param {string} status - Save status ('saving', 'saved', 'error')
   * @param {string} message - Optional error message
   * @param {string} fieldId - Optional field ID being saved
   * @private
   */
  _notifySaveStatus(status, message = null, fieldId = null) {
    this.store.notifySubscribers('saveStatus', {
      status,
      message,
      fieldId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Handle save errors
   * @param {Error} error - Error object
   * @param {string} fieldId - Optional field ID that caused the error
   * @private
   */
  _handleSaveError(error, fieldId = null) {
    console.error('Save failed:', error);
    this.store.handleError('companyQuestions', error);
    this._notifySaveStatus('error', error.message, fieldId);

    // Clear field lock if it exists
    if (fieldId) {
      this.fieldLocks.delete(fieldId);
    }

    // Reset saving flag
    this.isSaving = false;
  }

  /**
   * Check if a specific field is currently being saved
   * @param {string} fieldId - Field ID to check
   * @returns {boolean} True if the field is being saved
   */
  isFieldSaving(fieldId) {
    return this.fieldLocks.has(fieldId);
  }

  /**
   * Force immediate save if there's anything in the queue
   * Used primarily for cleanup/unmount operations
   * @returns {Promise<Object>} The current data after save attempt
   */
  async forceSave() {
    // Clear any existing timer
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }

    // If there's something in the queue, save it immediately
    if (this.saveQueue) {
      return await this._performSave();
    }

    return this.store.state.data.companyQuestions.data;
  }
}